import React, { ReactNode, useState } from 'react';

import { PerfectScrollbar } from '@src/components/PerfectScrollbar';

import { XCLUSION_SELECTIONS } from '@src/types/Filters';
import { SOURCE_NETWORKS } from '@src/types/SourceNetwork';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { FeatureDisabler } from '@src/components/FeatureDisabler';
import { classes, stylesheet } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import { CollectionSuggestion } from '@src/fragments/Filters/Search/Suggestions/CollectionSuggestion';
import { RightRequestMQLStatus } from '@adalong/schemas';
import { RightRequestStatusItem } from '@src/fragments/Filters/Search/FilterItem/RightRequestStatusItem';
import styles from './styles.module.scss';
import { FilterSection } from './FilterSection';
import { CheckBoxLine } from './CheckBoxLine';
import { RadioLine } from './RadioLine';
import { DateFilter } from './DateFilter';
import { LightSearchBar } from '@src/components/LightSearchBar';
import { Row } from '@src/components/Utils';
import { XclusionSwitch } from './Search/XclusionSwitch';
import { LanguageSuggestion } from './Search/Suggestions/LanguageSuggestion';
import { CreatorsSuggestion } from './Search/Suggestions/CreatorsSuggestion';
import { OwnContentSuggestion } from './Search/Suggestions/OwnContentSuggestion';
import { getSelectedSuggestion, selectCustomVariant, selectNetwork, SourcesSuggestion } from './Search/Suggestions/SourcesSuggestion';
import { MediaTypeItem } from './Search/FilterItem/MediaTypeItem';
import { FilterNote } from './FilterNote';
import { FilterSelections } from './FilterSelections';
import { ProductsSuggestion } from './Search/Suggestions/ProductsSuggestion';
import { InfluencerSuggestion } from '@src/fragments/Filters/Search/Suggestions/InfluencerSuggestion';
import { useAtom, useAtomValue } from 'jotai';
import {
  FacetState,
  filterCollectionInputState,
  filterCollectionXclusionState,
  filterCreatorInputState,
  filterCreatorCountriesInputState,
  filterCreatorCountriesXclusionState,
  filterCreatorXclusionState,
  filterLanguageInputState,
  filterAudioLanguageInputState,
  filterLanguageXclusionState,
  filterOwnContentXclusionState,
  filterProductCatalogCountriesInputState,
  filterProductExactXclusionState,
  filterProductSimilarXclusionState,
  filterSourceInputState,
} from '@src/pages/Discover/components/Atoms/Atoms';
import {
  clearFilter,
  onFilterChange,
  onXclusionSwitch,
} from '@src/fragments/Filters/utils/FilterStateUtility';
import { ExcludeLabelsSwitch } from '@src/components/ExcludeLabelsSwitch';
import { CreatorCountriesSuggestion } from '@src/fragments/Filters/Search/Suggestions/CreatorCountriesSuggestion';
import { CatalogCountriesSuggestion } from '@src/fragments/Filters/Search/Suggestions/CatalogCountriesSuggestion';
import { AudioLanguageSuggestion } from '@src/fragments/Filters/Search/Suggestions/AudioLanguageSuggestion';
import { isMySelectionSectionFilled } from '@src/utils/QS';
import { defaultFiltersParams, useFiltersParams } from '@src/hooks/useFiltersParams';
import Tooltip from '@mui/material/Tooltip';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import RadioGroup from '@mui/material/RadioGroup';

export enum FilterKey {
  collection = 'collection',
}

interface CheckBoxProps {
  status: RightRequestMQLStatus;
  rrFilter: string[];
}

interface Props {
  enableProductFilter: boolean;
  enableAudioFilter?: boolean;
  catalogCountriesFilterValues: string[];
  creatorCountriesFilterValues: string[];
  hideFilters?: FilterKey[];
  topSlot?: ReactNode;
}

const excludeLabels = ['advertising', 'graphic design', 'graphics', 'trademark', 'publication', 'signage', 'drawing'];

export function FiltersView(props: Props) {
  const { enableProductFilter, enableAudioFilter = false, hideFilters } = props;
  const hiddenFilters = new Set(hideFilters);
  const isHidden = (filterKey: FilterKey) => hiddenFilters.has(filterKey);

  const { filters, setFilters } = useFiltersParams();
  const facetState = useAtomValue(FacetState);
  const [filterProductExactXclusion, setfilterProductExactXclusion] = useAtom(filterProductExactXclusionState);
  const [filterProductSimilarXclusion, setfilterProductSimilarXclusion] = useAtom(filterProductSimilarXclusionState);
  const [filterLanguageInput, setFilterLanguageInput] = useAtom(filterLanguageInputState);
  const [filterAudioLanguageInput, setFilterAudioLanguageInput] = useAtom(filterAudioLanguageInputState);
  const [filterLanguageXclusion, setFilterLanguageXclusion] = useAtom(filterLanguageXclusionState);
  const [filterCreatorXclusion, setFilterCreatorXclusion] = useAtom(filterCreatorXclusionState);
  const [filterOwnContentXclusion, setFilterOwnContentXclusion] = useAtom(filterOwnContentXclusionState);
  const [filterCollectionXclusion, setFilterCollectionXclusion] = useAtom(filterCollectionXclusionState);
  const [filterSourceInput, setFilterSourceInput] = useAtom(filterSourceInputState);
  const [filterCreatorInput, setFilterCreatorInput] = useAtom(filterCreatorInputState);
  const [filterCollectionInput, setFilterCollectionInput] = useAtom(filterCollectionInputState);
  const [filterProductCatalogCountriesInput, setFilterProductCatalogCountriesInput] = useAtom(filterProductCatalogCountriesInputState);
  const [filterCreatorCountriesInput, setFilterCreatorCountriesInput] = useAtom(filterCreatorCountriesInputState);
  const [filterCreatorCountriesXclusion, setFilterCreatorCountriesXclusion] = useAtom(filterCreatorCountriesXclusionState);
  const filterState = filters;
  const selectedSources = getSelectedSuggestion(filterState.source);

  // Exclude labels
  const [filterLabels, setFilterLabels] = useState(false);
  const handleLabelSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterLabels(event.target.checked);
    if (event.target.checked) {
      // Include all labels
      onFilterChange(setFilters, 'label')(excludeLabels, true, XCLUSION_SELECTIONS[1]);
    } else {
      // Clear all labels
      clearFilter(setFilters, 'label');
    }
  };

  const handleClearAll = () => {
    setFilters(defaultFiltersParams);
  };

  const rrStatusList = [RightRequestMQLStatus.Agreed, RightRequestMQLStatus.Requested, RightRequestMQLStatus.Unrequested];
  const autoExcludeCollection = facetState.mediaConstraints.collections?.find((coll) => coll.name.includes('EXCLUDE'));
  const [autoXclusion, setAutoXclusion] = useState(false);
  const changeAutoXclusion = (key: string) => {
    const changeCollectionFilter = onFilterChange(setFilters, 'collection');
    changeCollectionFilter([key], !autoXclusion, XCLUSION_SELECTIONS[1]);
    setAutoXclusion(!autoXclusion);
  };

  const RightRequestCheckBox = ({ status, rrFilter }: CheckBoxProps) => (
    <CheckBoxLine checked={rrFilter.includes(status)} onChange={(checked: boolean) => onFilterChange(setFilters, 'rrStatus')([status], checked)}>
      <RightRequestStatusItem rrStatus={status} />
    </CheckBoxLine>
  );

  const isSelectionFilled = isMySelectionSectionFilled(filterState);

  return (
    <PerfectScrollbar
      className={styles.filters}
      style={{ height: '100%' }}
      options={{
        wheelSpeed: 0.3,
        minScrollbarLength: 30,
      }}
    >
      <div className={classes('filters-container', sheet.container)}>
        {props.topSlot ? (
          <div className={sheet.topSlot}>{props.topSlot}</div>
        ) : null}
        <FilterSection
          displayClearButton={isSelectionFilled}
          title={lang.selection.title}
          indentation={isSelectionFilled ? 0 : 1}
          onClear={() => handleClearAll()}
        >
          <FilterSelections />
        </FilterSection>
        <FilterSection title={lang.dates.title} displayClearButton={!!filters.date.length} onClear={() => clearFilter(setFilters, 'date')}>
          <DateFilter />
        </FilterSection>
        <FilterSection title={lang.network.title} displayClearButton={!!filters.source.length} onClear={() => clearFilter(setFilters, 'source')}>
          {SOURCE_NETWORKS.map((network) => (
            network === 'custom' ? (
              <React.Fragment key={network}>
                <CheckBoxLine
                  checked={!!selectedSources.find((s) => s.network === 'custom' && !s.variant)}
                  onChange={() => selectNetwork(network, facetState.mediaConstraints.state, filters.source, setFilters)}
                >
                  {lang.network.values[network]}
                </CheckBoxLine>
                <CheckBoxLine
                  checked={!!selectedSources.find((s) => s.network === 'custom' && s.variant === 'customer')}
                  onChange={() => selectCustomVariant('customer', facetState.mediaConstraints.state, filters.source, setFilters)}
                >
                  {lang.network.values.customer}
                </CheckBoxLine>
                <CheckBoxLine
                  checked={!!selectedSources.find((s) => s.network === 'custom' && s.variant === 'visitor')}
                  onChange={() => selectCustomVariant('visitor', facetState.mediaConstraints.state, filters.source, setFilters)}
                >
                  {lang.network.values.visitor}
                </CheckBoxLine>
              </React.Fragment>
            ) : (
              <CheckBoxLine
                checked={!!selectedSources.find((s) => s.network === network)}
                onChange={() => selectNetwork(network, facetState.mediaConstraints.state, filters.source, setFilters)}
                key={network}
              >
                {lang.network.values[network]}
              </CheckBoxLine>
            )
          ))}
        </FilterSection>
        <FilterSection title={lang.sources.title} displayClearButton={!!filters.source.length} onClear={() => clearFilter(setFilters, 'source')}>
          <Row>
            {/* TODO: get and set input from parents for queries */}
            <LightSearchBar
              onText={(v) => setFilterSourceInput(v)}
              value={filterSourceInput}
              isLoading={!!filterSourceInput.length}
              title={lang.sources.inputTitle}
            />
          </Row>
          <SourcesSuggestion />
        </FilterSection>
        <FilterSection
          title={lang.rightRequest.title}
          displayClearButton={!!filters.rrStatus.length}
          onClear={() => clearFilter(setFilters, 'rrStatus')}
        >
          {rrStatusList.map((status) => (
            <RightRequestCheckBox key={status} status={status} rrFilter={filters.rrStatus} />
          ))}
        </FilterSection>
        <FilterSection title={lang.media.title} displayClearButton={!!filters.media.length} onClear={() => clearFilter(setFilters, 'media')}>
          <CheckBoxLine
            checked={filters.media.includes('video')}
            onChange={(checked: boolean) => onFilterChange(setFilters, 'media')(['video'], checked)}
          >
            <MediaTypeItem type='video' />
          </CheckBoxLine>
          <CheckBoxLine
            checked={filters.media.includes('image')}
            onChange={(checked: boolean) => onFilterChange(setFilters, 'media')(['image'], checked)}
          >
            <MediaTypeItem type='image' />
          </CheckBoxLine>
        </FilterSection>
        {enableAudioFilter ? (
          <FilterSection
            title={lang.audio.title}
            displayClearButton={!!filters.audio.length}
            onClear={() => clearFilter(setFilters, 'audio')}
          >
            <RadioGroup
              value={filters.audio[0] || ''}
              onChange={(event) => setFilters((current) => ({ ...current, audio: [event.target.value] }))}
            >
              <RadioLine
                value="music"
                checked={filters.audio.includes('music')}
                onChange={(value) => setFilters((current) => ({ ...current, audio: value ? [value] : [] }))}
              >
                {lang.audio.values.music}
              </RadioLine>
              <RadioLine
                value="voice"
                checked={filters.audio.includes('voice')}
                onChange={(value) => setFilters((current) => ({ ...current, audio: value ? [value] : [] }))}
              >
                {lang.audio.values.voice}
              </RadioLine>
              <RadioLine
                value="music_voice"
                checked={filters.audio.includes('music_voice')}
                onChange={(value) => setFilters((current) => ({ ...current, audio: value ? [value] : [] }))}
              >
                {lang.audio.values.musicVoice}
              </RadioLine>
              <RadioLine
                value="none"
                checked={filters.audio.includes('none')}
                onChange={(value) => setFilters((current) => ({ ...current, audio: value ? [value] : [] }))}
              >
                {lang.audio.values.none}
              </RadioLine>
            </RadioGroup>
          </FilterSection>
        ) : null}
        {enableAudioFilter ? (
          <FilterSection
            title={lang.audioLanguage.title}
            displayClearButton={!!filters.audioLang.length}
            onClear={() => clearFilter(setFilters, 'audioLang')}
          >
            <Row>
              <LightSearchBar
                onText={(value) => setFilterAudioLanguageInput(value)}
                value={filterAudioLanguageInput}
                title={lang.audioLanguage.inputTitle}
                inputAriaLabel={lang.audioLanguage.inputTitle}
              />
            </Row>
            <AudioLanguageSuggestion />
          </FilterSection>
        ) : null}
        {autoExcludeCollection && !isHidden(FilterKey.collection) ? (
          <FilterSection
            title={lang.autoExclude.title}
            displayClearButton={!!filters.source.length}
            onClear={() => clearFilter(setFilters, 'source')}
          >
            <CheckBoxLine checked={autoXclusion} onChange={() => changeAutoXclusion(autoExcludeCollection?._id)} key={'autoExclude'}>
              {'Exclude Collection'}
            </CheckBoxLine>
          </FilterSection>
        ) : null}
        {!isHidden(FilterKey.collection) ? (
          <FilterSection
            title={lang.collection.title}
            displayClearButton={filters.collection.length > 0}
            onClear={() => clearFilter(setFilters, 'collection')}
          >
            <XclusionSwitch
              selected={filterCollectionXclusion}
              onChange={(val) => onXclusionSwitch(val, setFilterCollectionXclusion, 'collection', setFilters)}
            />
            <Row>
              <LightSearchBar onText={(v) => setFilterCollectionInput(v)} value={filterCollectionInput} title={lang.collection.inputTitle} />
            </Row>
            <CollectionSuggestion />
          </FilterSection>
        ) : null}
        <FilterSection title={lang.language.title} displayClearButton={!!filters.lang.length} onClear={() => clearFilter(setFilters, 'lang')}>
          <XclusionSwitch
            selected={filterLanguageXclusion}
            onChange={(val) => onXclusionSwitch(val, setFilterLanguageXclusion, 'lang', setFilters)}
          />
          <Row>
            <LightSearchBar
              onText={(v) => setFilterLanguageInput(v)}
              value={filterLanguageInput}
              isLoading={!!filterLanguageInput.length}
              title={lang.language.inputTitle}
            />
          </Row>
          <LanguageSuggestion />
        </FilterSection>
        <FeatureDisabler disabled={!enableProductFilter} tooltip={lang.premiumFeatureWarning}>
          <FilterSection
            title={lang.products.title}
            displayClearButton={!!filters.productExact.length}
            onClear={() => clearFilter(setFilters, 'productExact')}
            disabled={!enableProductFilter}
          >
            <XclusionSwitch
              selected={filterProductExactXclusion}
              onChange={(val) => onXclusionSwitch(val, setfilterProductExactXclusion, 'productExact', setFilters)}
            />
            <ProductsSuggestion productType='productExact' inputTitle={lang.products.inputTitle} />
          </FilterSection>
          <FilterSection
            title={lang.similarProducts.title}
            displayClearButton={!!filters.productSimilar.length}
            onClear={() => clearFilter(setFilters, 'productSimilar')}
            disabled={!enableProductFilter}
          >
            <XclusionSwitch
              selected={filterProductSimilarXclusion}
              onChange={(val) => onXclusionSwitch(val, setfilterProductSimilarXclusion, 'productSimilar', setFilters)}
            />
            <ProductsSuggestion productType='productSimilar' inputTitle={lang.similarProducts.inputTitle} />
          </FilterSection>
        </FeatureDisabler>
        <FilterSection
          title={lang.influencers.title}
          displayClearButton={!!filters.creator.length}
          onClear={() => clearFilter(setFilters, 'creator')}
        >
          <XclusionSwitch
            selected={filterCreatorXclusion}
            onChange={(val) => onXclusionSwitch(val, setFilterCreatorXclusion, 'creator', setFilters)}
          />
          <InfluencerSuggestion />
          <FilterNote marginTop={15}>{lang.creators.info}</FilterNote>
        </FilterSection>
        <FilterSection title={lang.creators.title} displayClearButton={!!filters.creator.length} onClear={() => clearFilter(setFilters, 'creator')}>
          <XclusionSwitch
            selected={filterCreatorXclusion}
            onChange={(val) => onXclusionSwitch(val, setFilterCreatorXclusion, 'creator', setFilters)}
          />
          <Row>
            <LightSearchBar
              onText={(v) => setFilterCreatorInput(v)}
              value={filterCreatorInput}
              isLoading={!!filterCreatorInput.length}
              title={lang.creators.inputTitle}
            />
          </Row>
          <CreatorsSuggestion />
          <FilterNote marginTop={15}>{lang.creators.info}</FilterNote>
        </FilterSection>
        <FilterSection
          title={lang.selfaccounts.title}
          displayClearButton={!!filters.ownContent.length}
          onClear={() => clearFilter(setFilters, 'ownContent')}
        >
          <XclusionSwitch
            selected={filterOwnContentXclusion}
            onChange={(val) => onXclusionSwitch(val, setFilterOwnContentXclusion, 'ownContent', setFilters)}
          />
          <OwnContentSuggestion />
          <FilterNote marginTop={15}>{lang.selfaccounts.info}</FilterNote>
        </FilterSection>
        {props.creatorCountriesFilterValues.length > 0 ?
          <FilterSection
            title={lang.creatorCountries.title}
            afterTitle={
              <Tooltip title={lang.creatorCountries.info} placement="top">
                <span className={sheet.infoIcon}>
                  <InfoSvgIcon />
                </span>
              </Tooltip>
            }
            displayClearButton={!!filters.creatorCountries.length}
            onClear={() => clearFilter(setFilters, 'creatorCountries')}
          >
            <XclusionSwitch
              selected={filterCreatorCountriesXclusion}
              onChange={(val) => onXclusionSwitch(val, setFilterCreatorCountriesXclusion, 'creatorCountries', setFilters)}
            />
            <Row>
              <LightSearchBar
                onText={(v) => setFilterCreatorCountriesInput(v)}
                value={filterCreatorCountriesInput}
                isLoading={!!filterCreatorCountriesInput.length}
                title={lang.creatorCountries.inputTitle}
              />
            </Row>
            <CreatorCountriesSuggestion availableCountries={props.creatorCountriesFilterValues} />
          </FilterSection> : null}
        <FeatureDisabler disabled={!enableProductFilter} tooltip={lang.premiumFeatureWarning}>
          {props.catalogCountriesFilterValues.length > 0 ? (
            <FilterSection
              title={lang.catalogCountries.title}
              afterTitle={
                <Tooltip title={lang.catalogCountries.info} placement="top">
                  <span className={sheet.infoIcon}>
                    <InfoSvgIcon />
                  </span>
                </Tooltip>
              }
              displayClearButton={!!filters.productCatalogCountries.length}
              onClear={() => clearFilter(setFilters, 'productCatalogCountries')}
              disabled={!enableProductFilter}
            >
              <Row>
                <LightSearchBar
                  onText={(v) => setFilterProductCatalogCountriesInput(v)}
                  value={filterProductCatalogCountriesInput}
                  isLoading={!!filterProductCatalogCountriesInput.length}
                  title={lang.catalogCountries.inputTitle}
                />
              </Row>
              <CatalogCountriesSuggestion availableCountries={props.catalogCountriesFilterValues} />
            </FilterSection>
          ) : null}
        </FeatureDisabler>
        <ExcludeLabelsSwitch checked={filterLabels} onLabelSwitchChange={handleLabelSwitchChange} />
      </div>
    </PerfectScrollbar>
  );
}

const lang = LocalizedStrings({
  en: {
    selection: {
      title: 'Filters applied',
    },
    media: {
      title: 'Media',
    },
    audio: {
      title: 'Audio Type',
      values: {
        music: 'Music Only',
        voice: 'Voice Only',
        musicVoice: 'Music and Voice',
        none: 'None',
      },
    },
    audioLanguage: {
      title: 'Voice Language',
      inputTitle: 'filter by audio language',
    },
    exclude: {
      title: 'Exclude labels',
    },
    rightRequest: {
      title: 'Right Requests',
    },
    dates: {
      title: 'Dates',
    },
    collection: {
      title: 'Collections',
      inputTitle: 'filter by collection',
    },
    autoExclude: {
      title: 'Auto Exclude',
      inputTitle: 'exclude default collection',
    },
    language: {
      title: 'Language',
      inputTitle: 'filter by language',
    },
    network: {
      title: 'Network',
      values: {
        instagram: 'Instagram',
        twitter: 'Twitter',
        tiktok: 'TikTok',
        custom: 'Custom',
        customer: 'Brand asset',
        visitor: 'Visitor upload',
        default: 'Default',
      },
    },
    selfaccounts: {
      title: 'Your Content',
      info: 'filter your content',
    },
    sources: {
      title: 'Sources',
      inputTitle: 'filter by source',
    },
    products: {
      title: 'Exact Products Linked',
      inputTitle: 'filter by SKU or Reference',
    },
    similarProducts: {
      title: 'Similar Products Linked',
      inputTitle: 'filter by SKU or Reference',
    },
    catalogCountries: {
      title: 'Catalog Countries',
      inputTitle: 'filter by catalog countries',
      info: 'Media linked to at least one exact product from the selected catalog countries',
    },
    creators: {
      title: 'Creators*',
      inputTitle: 'filter by username',
      info: '* N/A for Instagram hashtagged media',
    },
    influencers: {
      title: 'Influencers*',
      inputTitle: 'filter by username',
      info: '* N/A for Instagram hashtagged media',
    },
    creatorCountries: {
      title: 'Creator Countries',
      inputTitle: 'filter by creation countries',
      info: 'Media whose creator is linked to the selected countries',
    },
    premiumFeatureWarning: 'This is a premium feature. Please contact us for more information.',
  },
});

const sheet = stylesheet({
  container: {
    color: ADALONG_COLORS.LIGHT_GRAY,
    fontWeight: 400,
  },
  topSlot: {
    marginBottom: 12,
  },
  infoIcon: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginLeft: 8,
    color: ADALONG_COLORS.LIGHT_GRAY,
    cursor: 'help',
  },
});
